import { useCallback, useEffect, useState } from "react";
import { FiDownload, FiEdit2, FiPlus, FiTrash2 } from "react-icons/fi";
import codeBankNbfcService from "../../../services/codeBankNbfc.service";
import { getApiErrorMessage, saveExcelBlob } from "../../../utils/downloadExcel";
import "../../../styles/users.css";
import "../../../styles/inventoryUpdate.css";
import "../../../styles/bankDetails.css";
import "../../../styles/codeBankNbfc.css";

const EMPTY_FORM = {
  bankName: "",
  agreementDate: "",
  agreementExpireDate: "",
  code: "",
  operatingManager: "",
  mobileNo: "",
  mail: "",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function formatDisplayDate(value) {
  if (!value) return "—";
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-IN");
}

export default function CodeBankNbfc() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [success, setSuccess] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [pendingId, setPendingId] = useState("");

  const loadRecords = useCallback(async () => {
    try {
      setLoading(true);
      setListError("");
      const res = await codeBankNbfcService.list();
      setRecords(res?.data?.data?.records || []);
    } catch (err) {
      setListError(await getApiErrorMessage(err, "Failed to load records."));
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const closeForm = () => {
    setOpen(false);
    setForm(EMPTY_FORM);
    setFormError("");
    setSaving(false);
    setEditingId("");
  };

  const openForm = () => {
    setForm(EMPTY_FORM);
    setFormError("");
    setSuccess("");
    setEditingId("");
    setOpen(true);
  };

  const openEdit = (row) => {
    setForm({
      bankName: row.bankName || "",
      agreementDate: row.agreementDate || "",
      agreementExpireDate: row.agreementExpireDate || "",
      code: row.code || "",
      operatingManager: row.operatingManager || "",
      mobileNo: row.mobileNo || "",
      mail: row.mail || "",
    });
    setFormError("");
    setSuccess("");
    setEditingId(row.id);
    setOpen(true);
  };

  const setField = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleDownloadExcel = async () => {
    try {
      setDownloading(true);
      setListError("");
      const response = await codeBankNbfcService.downloadExcel();
      await saveExcelBlob(response, "code-bank-nbfc.xlsx");
    } catch (err) {
      setListError(await getApiErrorMessage(err, "Failed to download Excel."));
    } finally {
      setDownloading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const bankName = form.bankName.trim();
    const code = form.code.trim();
    const operatingManager = form.operatingManager.trim();
    const mobileNo = form.mobileNo.trim();
    const mail = form.mail.trim();

    if (!bankName) {
      setFormError("Bank / NBFC Name is required.");
      return;
    }
    if (!code) {
      setFormError("Code is required.");
      return;
    }
    if (!operatingManager) {
      setFormError("Operating Manager is required.");
      return;
    }
    if (!mobileNo) {
      setFormError("Mobile No is required.");
      return;
    }
    if (mobileNo.replace(/\D/g, "").length < 10) {
      setFormError("Mobile No must have at least 10 digits.");
      return;
    }
    if (!mail) {
      setFormError("Mail is required.");
      return;
    }
    if (!EMAIL_RE.test(mail)) {
      setFormError("Mail must be a valid email.");
      return;
    }
    if (
      form.agreementDate &&
      form.agreementExpireDate &&
      form.agreementExpireDate < form.agreementDate
    ) {
      setFormError("Agreement Expire Date cannot be before Agreement Date.");
      return;
    }

    const payload = {
      bankName,
      agreementDate: form.agreementDate || null,
      agreementExpireDate: form.agreementExpireDate || null,
      code,
      operatingManager,
      mobileNo,
      mail,
    };

    try {
      setSaving(true);
      setFormError("");
      if (editingId) {
        await codeBankNbfcService.update(editingId, payload);
        closeForm();
        setSuccess("Code Details Bank / NBFC updated.");
      } else {
        await codeBankNbfcService.create(payload);
        closeForm();
        setSuccess("Code Details Bank / NBFC saved.");
      }
      await loadRecords();
    } catch (err) {
      setFormError(await getApiErrorMessage(err, "Failed to save record."));
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (
      !window.confirm(
        `Delete "${row.bankName}"? This cannot be undone.`
      )
    ) {
      return;
    }
    try {
      setPendingId(row.id);
      setListError("");
      await codeBankNbfcService.remove(row.id);
      setSuccess("Code Details Bank / NBFC deleted.");
      await loadRecords();
    } catch (err) {
      setListError(await getApiErrorMessage(err, "Failed to delete record."));
    } finally {
      setPendingId("");
    }
  };

  return (
    <div className="page">
      <div className="cbn-header">
        <h2>Code Details Bank / NBFC</h2>
        <div className="cbn-header-actions">
          <button
            type="button"
            className="btn-table-excel"
            onClick={handleDownloadExcel}
            disabled={downloading || loading}
          >
            <FiDownload aria-hidden />
            <span>{downloading ? "Preparing file…" : "Download"}</span>
          </button>
          <button type="button" className="primary-page-btn" onClick={openForm}>
            <FiPlus /> Add
          </button>
        </div>
      </div>

      {success ? <p className="inv-success">{success}</p> : null}
      {listError ? <p className="error-text">{listError}</p> : null}

      {loading ? (
        <p className="muted">Loading records…</p>
      ) : records.length === 0 ? (
        <p className="muted">No Code Details Bank / NBFC records yet.</p>
      ) : (
        <div className="company-table-wrap bd-excel-sheet">
          <table className="users-table excel-grid-table bd-excel-table">
            <thead>
              <tr>
                <th>Bank / NBFC Name</th>
                <th>Agreement Date</th>
                <th>Agreement Expire Date</th>
                <th>Code</th>
                <th>Operating Manager</th>
                <th>Mobile No</th>
                <th>Mail</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {records.map((row) => (
                <tr key={row.id}>
                  <td>{row.bankName}</td>
                  <td>{formatDisplayDate(row.agreementDate)}</td>
                  <td>{formatDisplayDate(row.agreementExpireDate)}</td>
                  <td>{row.code}</td>
                  <td>{row.operatingManager}</td>
                  <td>{row.mobileNo}</td>
                  <td>{row.mail}</td>
                  <td>
                    <div className="cbn-actions">
                      <button
                        type="button"
                        className="cbn-icon-btn"
                        title="Edit"
                        aria-label={`Edit ${row.bankName}`}
                        disabled={pendingId === row.id}
                        onClick={() => openEdit(row)}
                      >
                        <FiEdit2 />
                      </button>
                      <button
                        type="button"
                        className="cbn-icon-btn cbn-icon-btn--danger"
                        title="Delete"
                        aria-label={`Delete ${row.bankName}`}
                        disabled={pendingId === row.id}
                        onClick={() => handleDelete(row)}
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open ? (
        <div className="modal-overlay" onClick={closeForm} role="presentation">
          <div
            className="modal-box cbn-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="cbn-form-title"
          >
            <h3 id="cbn-form-title">
              {editingId ? "Edit Code Details Bank / NBFC" : "Add Code Details Bank / NBFC"}
            </h3>
            <form onSubmit={handleSubmit}>
              <div className="cbn-form-grid">
                <div className="cbn-span-2">
                  <label className="modal-label" htmlFor="cbn-bank-name">
                    Bank / NBFC Name
                  </label>
                  <input
                    id="cbn-bank-name"
                    className="modal-input"
                    value={form.bankName}
                    onChange={setField("bankName")}
                    required
                  />
                </div>
                <div>
                  <label className="modal-label" htmlFor="cbn-agreement-date">
                    Agreement Date
                  </label>
                  <input
                    id="cbn-agreement-date"
                    type="date"
                    className="modal-input"
                    value={form.agreementDate}
                    onChange={setField("agreementDate")}
                  />
                </div>
                <div>
                  <label className="modal-label" htmlFor="cbn-expire-date">
                    Agreement Expire Date
                  </label>
                  <input
                    id="cbn-expire-date"
                    type="date"
                    className="modal-input"
                    value={form.agreementExpireDate}
                    onChange={setField("agreementExpireDate")}
                  />
                </div>
                <div>
                  <label className="modal-label" htmlFor="cbn-code">
                    Code
                  </label>
                  <input
                    id="cbn-code"
                    className="modal-input"
                    value={form.code}
                    onChange={setField("code")}
                    required
                  />
                </div>
                <div>
                  <label className="modal-label" htmlFor="cbn-manager">
                    Operating Manager
                  </label>
                  <input
                    id="cbn-manager"
                    className="modal-input"
                    value={form.operatingManager}
                    onChange={setField("operatingManager")}
                    required
                  />
                </div>
                <div>
                  <label className="modal-label" htmlFor="cbn-mobile">
                    Mobile No
                  </label>
                  <input
                    id="cbn-mobile"
                    className="modal-input"
                    type="tel"
                    value={form.mobileNo}
                    onChange={setField("mobileNo")}
                    required
                  />
                </div>
                <div>
                  <label className="modal-label" htmlFor="cbn-mail">
                    Mail
                  </label>
                  <input
                    id="cbn-mail"
                    className="modal-input"
                    type="email"
                    value={form.mail}
                    onChange={setField("mail")}
                    required
                  />
                </div>
              </div>
              {formError ? (
                <p className="error-text cbn-form-error">{formError}</p>
              ) : null}
              <div className="modal-actions">
                <button
                  type="button"
                  className="secondary-page-btn"
                  onClick={closeForm}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button type="submit" className="primary-page-btn" disabled={saving}>
                  {saving ? "Saving…" : editingId ? "Update" : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
